import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    TablePagination, Fab, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button,
    Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, IconButton, Box, CircularProgress,
    RadioGroup, FormControlLabel, Radio, FormHelperText, Typography, Container
  } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchSystemUsers, addSystemUser, fetchUserGroups, deleteSystemUser } from "../utils/api";

const SystemUsers = () => {
  const navigate = useNavigate();
  
  // Check if user is super admin
  const isSuperAdmin = useMemo(() => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    const normalizedGroupName = userGroupName.toLowerCase().replace(/[\s_-]+/g, '');
    return normalizedGroupName === 'superadmin' || normalizedGroupName === 'super-admin';
  }, []);
  
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [newUser, setNewUser] = useState({ 
    firstName: "", 
    lastName: "", 
    mobile: "", 
    employeeCode: "",
    gender: "Male",
    username: "", 
    password: "",
    firebaseId: "null",
    status: "null",
    userGroup: {
      userGroupId: ""
    },
    reportsTo: "",
    target: ""
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loadingUserGroups, setLoadingUserGroups] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "userGroupId") {
      setNewUser(prev => ({
        ...prev,
        userGroup: {
          userGroupId: value
        }
      }));
    } else {
      setNewUser(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false); 
  }

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  // Fetch users from API - only for super admin
  useEffect(() => {
    // Only load users if user is super admin
    if (!isSuperAdmin) {
      setUsers([]);
      return;
    }
    
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setLoadingUserGroups(true);
      try {
        const response = await fetchSystemUsers();
        if (cancelled) return;
        console.log('Users response:', response.data);
        setUsers(response.data || []);

        const groupsResponse = await fetchUserGroups();
        if (cancelled) return;
        console.log('Groups response:', groupsResponse.data);
        if (!groupsResponse.data || groupsResponse.data.length === 0) {
          throw new Error('No user groups found');
        }
        setUserGroups(groupsResponse.data || []);

        setError(null);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!cancelled) {
          setError("Failed to load data. Please try again later.");
          showSnackbar("Failed to load data. Please try again later.", "error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingUserGroups(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setNewUser({ 
      firstName: "", 
      lastName: "", 
      mobile: "", 
      employeeCode: "",
      gender: "Male",
      username: "", 
      password: "",
      firebaseId: "null",
      status: "null",
      userGroup: {
        userGroupId: ""
      },
      reportsTo: "",
      target: ""
    });
  };

  const handleAddUser = async () => {
    try {
      if (!newUser.firstName || !newUser.lastName || !newUser.mobile || !newUser.employeeCode || 
          !newUser.username || !newUser.password || !newUser.userGroup.userGroupId) {
        showSnackbar("Please fill in all required fields", "error");
        return;
      }

      const submitData = {
        ...newUser,
        reportsTo: newUser.reportsTo ? Number(newUser.reportsTo) : null,
        target: newUser.target ? Number(newUser.target) : null
      };

      await addSystemUser(submitData, {
        identifier: getFullName(newUser) || newUser.username
      });
      const response = await fetchSystemUsers();
      setUsers(response.data || []);
      setOpen(false);
      showSnackbar("User added successfully!");
      handleClose();
    } catch (error) {
      console.error("Error adding user:", error);
      showSnackbar("Failed to add user. Please try again.", "error");
    }
  };

  const handleEdit = (userId) => {
    navigate(`/dashboard/system-users/edit/${userId}`);
  };

  const handleDelete = async (user) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteSystemUser(user.userId, {
          previousData: user,
          identifier: getFullName(user) || user.username
        });
        const response = await fetchSystemUsers();
        setUsers(response.data || []);
        showSnackbar("User deleted successfully!");
      } catch (error) {
        console.error("Error deleting user:", error);
        showSnackbar("Failed to delete user. Please try again.", "error");
      }
    }
  };

  const getFullName = (user) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
  };

  const getUserGroupName = (userGroup) => {
    if (!userGroup || !userGroup.userGroupId) return 'N/A';
    const group = userGroups.find(g => g.userGroupId === userGroup.userGroupId);
    return group ? group.groupName : 'N/A';
  };

  // If user is not super admin, show access denied message
  if (!isSuperAdmin) {
    return (
      <Container sx={{ mt: 5 }}>
        <Alert severity="warning">
          You do not have permission to view system users. Only super admin users can access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <div>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    
    <TableContainer component={Paper} sx={{ width: "90%", margin: "auto", mt: 5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h5" component="h2">System Users</Typography>
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleClickOpen}
          size="medium"
        >
          <AddIcon />
        </Fab>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Full Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Employee Code</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>User Group</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(users || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{getFullName(user)}</TableCell>
                  <TableCell>{user.mobile || 'N/A'}</TableCell>
                  <TableCell>{user.employeeCode || 'N/A'}</TableCell>
                  <TableCell>{user.username || 'N/A'}</TableCell>
                  <TableCell>{user.gender || 'N/A'}</TableCell>
                  <TableCell>{getUserGroupName(user.userGroup)}</TableCell>
                  <TableCell>
                    <IconButton 
                      color="primary" 
                      aria-label="edit" 
                      onClick={() => handleEdit(user.userId)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="secondary" 
                      aria-label="delete" 
                      onClick={() => handleDelete(user)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 15]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </TableContainer>

    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New System User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            name="firstName"
            fullWidth
            required
            value={newUser.firstName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Last Name"
            name="lastName"
            fullWidth
            required
            value={newUser.lastName}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Mobile Number"
            name="mobile"
            fullWidth
            required
            value={newUser.mobile}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Employee Code"
            name="employeeCode"
            fullWidth
            required
            value={newUser.employeeCode}
            onChange={handleInputChange}
          />
          <FormControl component="fieldset" margin="dense">
            <RadioGroup
              row
              name="gender"
              value={newUser.gender}
              onChange={handleInputChange}
            >
              <FormControlLabel value="Male" control={<Radio />} label="Male" />
              <FormControlLabel value="Female" control={<Radio />} label="Female" />
              <FormControlLabel value="Other" control={<Radio />} label="Other" />
            </RadioGroup>
          </FormControl>
          <TextField
            margin="dense"
            label="Username"
            name="username"
            fullWidth
            required
            value={newUser.username}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Password"
            name="password"
            type="password"
            fullWidth
            required
            value={newUser.password}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="user-group-label">User Group</InputLabel>
            <Select
              labelId="user-group-label"
              name="userGroupId"
              value={newUser.userGroup.userGroupId}
              label="User Group"
              onChange={handleInputChange}
              disabled={loadingUserGroups}
              error={Boolean(error)}
            >
              {loadingUserGroups ? (
                <MenuItem disabled>Loading user groups...</MenuItem>
              ) : userGroups.length === 0 ? (
                <MenuItem disabled>No user groups available</MenuItem>
              ) : (
                userGroups.map((group) => (
                  <MenuItem key={group.userGroupId} value={group.userGroupId}>
                    {group.groupName}
                  </MenuItem>
                ))
              )}
            </Select>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel id="reports-to-label">Reporting To</InputLabel>
            <Select
              labelId="reports-to-label"
              name="reportsTo"
              value={newUser.reportsTo || ""}
              label="Reporting To"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {users.map((adminUser) => (
                <MenuItem key={adminUser.userId} value={adminUser.userId}>
                  {`${adminUser.firstName || ''} ${adminUser.lastName || ''}`.trim() || adminUser.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Target"
            name="target"
            type="number"
            fullWidth
            value={newUser.target}
            onChange={handleInputChange}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">Cancel</Button>
          <Button onClick={handleAddUser} color="primary" variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SystemUsers;
